import { useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useSessionContext } from '@/context/SessionContextProvider';
import { useToast } from '@/hooks/use-toast';
import { toast as sonnerToast } from 'sonner';
import { X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { NewMessageToastContent } from '@/components/crm/inbox/NewMessageToastContent';
import React from 'react';
import { useAllChannelUserBindings } from './useChannelUsers';
import { useStoreAuth } from '@/context/StoreAuthContext';
import IncomingCallToast from '@/components/crm/IncomingCallToast';
import { startCallRingtone, stopCallRingtone } from '@/utils/callRingtone';

/**
 * Hook para notificações em tempo real do Inbox.
 * Toca som, mostra toast e atualiza título da aba.
 *
 * Pode ser usado de duas formas:
 * 1. Globalmente (sem args): notifica TODAS as mensagens inbound em qualquer página,
 *    EXCETO quando o usuário está em /crm/chat (nesse caso o próprio CrmInbox cuida).
 * 2. Na página do CRM: passar `selectedConversationId` para silenciar a conversa aberta.
 */
export const useInboxNotifications = (
  selectedConversationId?: string,
  totalUnread: number = 0
) => {
  const { networkId } = useSessionContext();
  const { user } = useStoreAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: allBindings = [] } = useAllChannelUserBindings();
  const isOnCrmChatPage = location.pathname.startsWith('/crm/chat');
  // Quando o hook é usado globalmente (sem selectedConversationId) e o usuário está
  // na página do CRM, fazemos early return para evitar listener duplicado.
  const isGlobalInstance = selectedConversationId === undefined;
  const shouldSkip = isGlobalInstance && isOnCrmChatPage;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const originalTitle = useRef(document.title);
  const lastToastAtRef = useRef(0);
  const countsRef = useRef<Map<string, number>>(new Map());
  const queueRef = useRef<string[]>([]);
  // conversa → id do aviso de chamada que está tocando
  const activeCallsRef = useRef<Map<string, string>>(new Map());

  // Ao abrir uma conversa, remove o card dela
  useEffect(() => {
    if (!selectedConversationId) return;
    const id = `crm-msg-${selectedConversationId}`;
    countsRef.current.delete(id);
    queueRef.current = queueRef.current.filter((q) => q !== id);
    sonnerToast.dismiss(id);
  }, [selectedConversationId]);

  // Create audio element for notification sound
  useEffect(() => {
    // Use a simple beep via AudioContext
    audioRef.current = null; // We'll use AudioContext instead
    return () => {
      document.title = originalTitle.current;
    };
  }, []);

  const playNotificationSound = useCallback(() => {
    try {
      const ctx = new AudioContext();
      const now = ctx.currentTime;

      const frequencies = [880, 587.33];
      const noteDuration = 0.08;
      const gap = 0.06;

      frequencies.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.type = 'sine';
        osc.frequency.value = freq;

        const start = now + i * (noteDuration + gap);
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(1.0, start + 0.005);
        gain.gain.linearRampToValueAtTime(0.6, start + noteDuration * 0.5);
        gain.gain.linearRampToValueAtTime(0, start + noteDuration);

        osc.start(start);
        osc.stop(start + noteDuration);
      });
    } catch {
      // Ignore audio errors
    }
  }, []);

  // Update document title with unread count
  useEffect(() => {
    if (shouldSkip) return;
    if (totalUnread > 0) {
      document.title = `(${totalUnread}) ${originalTitle.current}`;
    } else {
      document.title = originalTitle.current;
    }
  }, [totalUnread, shouldSkip]);

  // Listen for new inbound messages globally
  useEffect(() => {
    if (!networkId || shouldSkip) return;

    const channelName = isGlobalInstance
      ? 'crm-inbox-notifications-global'
      : 'crm-inbox-notifications-page';

    // Aviso de ligação: toca enquanto a ligação está "ringing", some quando muda.
    const handleCall = async (msg: any) => {
      const call = msg?.metadata?.call;
      if (!call?.call_id) return;
      const toastId = `crm-call-${call.call_id}`;
      // Ligação feita pela própria equipe: não é chamada recebida, sem aviso nem campainha.
      if (call.status !== 'ringing' || call.outgoing === true || msg.direction === 'outbound') {
        stopCallRingtone(toastId);
        sonnerToast.dismiss(toastId);
        return;
      }
      const { data: convData } = await supabase
        .from('crm_conversations')
        .select('id, channel_id, crm_contacts(name, phone, avatar_url), crm_channels(id, platform, name)')
        .eq('id', msg.conversation_id)
        .maybeSingle();
      const conv = convData as any;
      if (!conv) return; // RLS: sem acesso à conversa
      const channelId = conv.crm_channels?.id;
      if (channelId && user?.id) {
        const usersWithAccess = allBindings.filter(b => b.channel_id === channelId).map(b => b.user_id);
        if (usersWithAccess.length > 0 && !usersWithAccess.includes(user.id)) return;
      }
      const who = conv.crm_contacts?.name || conv.crm_contacts?.phone || 'Contato';
      const cleanup = () => {
        stopCallRingtone(toastId);
        if (activeCallsRef.current.get(msg.conversation_id) === toastId) {
          activeCallsRef.current.delete(msg.conversation_id);
        }
      };
      const close = () => {
        cleanup();
        sonnerToast.dismiss(toastId);
      };
      activeCallsRef.current.set(msg.conversation_id, toastId);
      startCallRingtone(toastId, 25000, close);
      sonnerToast.custom(
        () =>
          React.createElement(IncomingCallToast, {
            name: who,
            avatarUrl: conv.crm_contacts?.avatar_url,
            channelName: conv.crm_channels?.name,
            isVideo: !!call.is_video,
            onDismiss: close,
            onMute: () => stopCallRingtone(toastId),
            onOpen: () => {
              close();
              navigate(`/crm/chat?conversation=${msg.conversation_id}`);
            },
          }),
        {
          id: toastId,
          duration: 25000,
          unstyled: true,
          className: '!bg-transparent !border-0 !shadow-none !p-0',
          onDismiss: cleanup,
          onAutoClose: cleanup,
        }
      );
    };

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'crm_messages',
          filter: `network_id=eq.${networkId}`,
        },
        (payload) => {
          const msg = payload.new as any;
          if (msg?.metadata?.call) void handleCall(msg);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'crm_messages',
          filter: `network_id=eq.${networkId}`,
        },
        async (payload) => {
          const msg = payload.new as any;
          if (msg?.metadata?.call) {
            void handleCall(msg);
            return;
          }
          // Qualquer mensagem nova na conversa indica que a chamada já não está só tocando.
          if (msg?.conversation_id) {
            const activeCall = activeCallsRef.current.get(msg.conversation_id);
            if (activeCall) {
              stopCallRingtone(activeCall);
              sonnerToast.dismiss(activeCall);
              activeCallsRef.current.delete(msg.conversation_id);
            }
          }
          // Only notify for inbound messages not in the currently selected conversation
          if (msg.direction === 'inbound' && msg.conversation_id !== selectedConversationId) {

            // Realtime de crm_conversations já patcha a lista; só invalida o badge global.
            if (isGlobalInstance) {
              queryClient.invalidateQueries({ queryKey: ['crm-unread-count'] });
            }

            // Tenta achar a conversa em qualquer cache de crm-conversations antes de ir ao banco
            let contact: any = null;
            let channelInfo: any = null;
            const caches = queryClient.getQueriesData<any[]>({ queryKey: ['crm-conversations'] });
            for (const [, list] of caches) {
              if (!Array.isArray(list)) continue;
              const found = list.find((c: any) => c?.id === msg.conversation_id);
              if (found) {
                contact = found.crm_contacts;
                channelInfo = found.crm_channels;
                break;
              }
            }

            try {
              if (!contact || !channelInfo) {
                const { data: convData } = await supabase
                  .from('crm_conversations')
                  .select('id, channel_id, crm_contacts(name, phone, avatar_url), crm_channels(id, platform, name)')
                  .eq('id', msg.conversation_id)
                  .maybeSingle();
                
                const conv = convData as any;
                contact = conv?.crm_contacts;
                channelInfo = conv?.crm_channels;
              }

              // --- SECURITY AUDIT: Verificação de acesso ao canal ---
              if (channelInfo?.id && user?.id) {
                const usersWithAccess = allBindings
                  .filter(b => b.channel_id === channelInfo.id)
                  .map(b => b.user_id);
                
                // Se o canal tem restrições e o usuário não está na lista, não notifica
                if (usersWithAccess.length > 0 && !usersWithAccess.includes(user.id)) {
                  console.log(`[useInboxNotifications] Bloqueando notificação: usuário ${user.id} não tem acesso ao canal ${channelInfo.id}`);
                  return;
                }
              }
              // ---------------------------------------------------

              // Som somente após confirmar acesso ao canal (máx. 1 a cada 1.5s)
              const nowSound = Date.now();
              if (nowSound - lastToastAtRef.current >= 1500) {
                lastToastAtRef.current = nowSound;
                playNotificationSound();
              }

              const preview = msg.content?.substring(0, 100) || 'Nova mensagem recebida';
              const toastId = `crm-msg-${msg.conversation_id}`;
              const count = (countsRef.current.get(toastId) || 0) + 1;
              countsRef.current.set(toastId, count);

              // Fila de até 3 cards simultâneos (mais novo por último)
              const queue = queueRef.current.filter((id) => id !== toastId);
              queue.push(toastId);
              while (queue.length > 3) {
                const old = queue.shift()!;
                countsRef.current.delete(old);
                sonnerToast.dismiss(old);
              }
              queueRef.current = queue;

              const clear = () => {
                countsRef.current.delete(toastId);
                queueRef.current = queueRef.current.filter((id) => id !== toastId);
              };

              sonnerToast.custom(
                (id) =>
                  React.createElement(
                    'div',
                    {
                      className:
                        'relative w-[356px] max-w-full cursor-pointer rounded-lg border border-border bg-background p-4 pr-8 shadow-lg hover:brightness-95 transition-all',
                      onClick: () => {
                        navigate(`/crm/chat?conversation=${msg.conversation_id}`);
                        clear();
                        sonnerToast.dismiss(id);
                      },
                    },
                    React.createElement(NewMessageToastContent, {
                      contactName: contact?.name ?? null,
                      contactPhone: contact?.phone ?? null,
                      avatarUrl: contact?.avatar_url ?? null,
                      platform: channelInfo?.platform ?? null,
                      channelName: channelInfo?.name ?? null,
                      preview,
                      count,
                    }),
                    React.createElement(
                      'button',
                      {
                        type: 'button',
                        'aria-label': 'Fechar',
                        className:
                          'absolute right-2 top-2 rounded-md p-1 text-muted-foreground hover:text-foreground',
                        onClick: (e: React.MouseEvent) => {
                          e.stopPropagation();
                          clear();
                          sonnerToast.dismiss(id);
                        },
                      },
                      React.createElement(X, { className: 'h-4 w-4' })
                    )
                  ),
                {
                  id: toastId,
                  duration: 5000,
                  onAutoClose: clear,
                  onDismiss: clear,
                  unstyled: true,
                  className: '!bg-transparent !border-0 !shadow-none !p-0',
                }
              );
            } catch (err) {
              console.error('[useInboxNotifications] Erro ao processar notificação:', err);
              toast({
                title: '💬 Nova mensagem',
                description: msg.content?.substring(0, 80) || 'Nova mensagem recebida',
              });
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [networkId, user?.id, allBindings, selectedConversationId, playNotificationSound, toast, queryClient, shouldSkip, isGlobalInstance, navigate]);
};
